
import { defineComponent, onMounted, computed, ref } from "vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import { useRoute } from "vue-router";
import { mainFormatter } from "@/utils/index";
import moment from "moment";
import objectPath from "object-path";
import InvestmentHistory from "@/components/widgets/lists/Widget5.vue";
import { Field, ErrorMessage, useForm } from "vee-validate";
import * as Yup from "yup";

export default defineComponent({
  name: "view-funding-round",
  components: {
    InvestmentHistory,
    Field,
    ErrorMessage,
  },
  setup() {
    const { resetForm, handleSubmit } = useForm({
      validationSchema: Yup.object({
        investor: Yup.string().required().label("Investor"),
      }),
    });
    const touched = ref<boolean>(false);
    const touch = () => {
      touched.value = true;
    };
    const investorId = ref<string | number>("");
    const loading = ref<boolean>(false);
    const store = useStore();
    const route = useRoute();
    const business = ref<Record<string, unknown>>({});
    const businesses = computed(() => store.getters.getBusinesses);
    const investmentAmount = ref<string>("");
    const fundingRound = ref<Record<string, unknown>>({});
    const op = computed(() => objectPath);
    const currencyCodeSymbolEnums = {
      dollar: "$",
      pound: "£",
      euro: "€",
    };
    const currencyCodeEnums = {
      dollar: "USD",
      pound: "GBP",
      euro: "EUR",
    };
    const fundingRoundExists = computed(() =>
      Object.keys(fundingRound.value).length ? true : false
    );
    const createInvestment = handleSubmit(async () => {
      if (!investmentAmount.value) return;
      loading.value = true;
      try {
        const response = await store.dispatch(
          Actions.CREATE_FUNDING_ROUND_INVESTMENT,
          {
            fundingRoundId: route.params.id,
            investment: investmentAmount.value,
            businessId: route.query.businessId,
            investorId: investorId.value,
          }
        );
        if (!response?.success) throw new Error(response?.data?.message);
        investmentAmount.value = "";
        await refresh();
        loading.value = false;
        store.commit("setAlert", {
          message: "Success",
          subMessage: "Investment Created",
          variant: "primary",
          duration: 4000,
          show: true,
        });
        investorId.value = ""
        touched.value = false
      } catch (err) {
        loading.value = false;
        store.commit("setAlert", {
          message: "Error",
          subMessage: err,
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    });
    const formatDate = (date) => moment(date).format("YYYY-MM-DD");
    const formatter = computed(() => mainFormatter);
    const refresh = async () => {
      return new Promise((resolve, reject) => {
        store
          .dispatch(Actions.GET_FOUNDER_BUSINESSES, {
            page: 1,
            perPage: 10,
          })
          .then((response) => {
            if (response !== true) throw new Error();
            business.value = businesses.value?.founderBusinesses.find(
              (b) => b.id == route.query.businessId
            );
            fundingRound.value =
              ((
                business.value?.fundingRounds as Array<Record<string, unknown>>
              )?.find((round) => round.id == route.params.id) as Record<
                string,
                unknown
              >) ?? {};
            resolve("");
          })
          .catch(() => {
            reject();
          });
      });
    };
    const investors = ref<Array<Record<string, unknown>>>([]);
    onMounted(async () => {
      if (!businesses.value?.founderBusinesses?.length) {
        await refresh();
      } else
        business.value = businesses.value?.founderBusinesses.find(
          (b) => b.id == route.query.businessId
        );
      fundingRound.value =
        ((
          business.value?.fundingRounds as Array<Record<string, unknown>>
        )?.find((round) => round.id == route.params.id) as Record<
          string,
          unknown
        >) ?? {};
      try {
        const response = await store.dispatch(
          Actions.BUSINESS_INVESTORS,
          route.query.businessId
        );
        if (response?.success) {
          investors.value = response?.data ?? [];
        } else throw new Error(response?.message);
      } catch (err) {
        store.commit("setAlert", {
          message: "Error",
          subMessage: err,
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
      setCurrentPageBreadcrumbs("Funding Round", ["Business Details"]);
    });
    return {
      touch,
      touched,
      investors,
      investorId,
      loading,
      formatter,
      business,
      route,
      fundingRound,
      fundingRoundExists,
      formatDate,
      currencyCodeEnums,
      currencyCodeSymbolEnums,
      op,
      investmentAmount,
      createInvestment,
    };
  },
});
